import { Check, Minus, Plus } from "lucide-react";

import { Badge, Button, Card, CardContent } from "@stocks/ui";
import type {
  ItemImageSearchResult,
  SearchItemsByImageResponse,
} from "@stocks/shared";

import {
  ItemCategoryBadge,
  ZoomableImage,
} from "@/entities/item";

interface ItemRecommendationsProps {
  data: SearchItemsByImageResponse;
  onSelectItem: (item: ItemImageSearchResult) => void;
  onCreateNew: () => void;
}

const SpecDiff = ({ diff }: { diff: ItemImageSearchResult["specDiff"] }) => {
  if (diff.matches === null) {
    return <span className='text-xs text-muted-foreground'>규격 비교 없음</span>;
  }
  return (
    <div className='flex flex-wrap items-center gap-1 text-xs'>
      <Badge variant={diff.matches ? "default" : "destructive"}>
        {diff.matches ? "규격 일치" : "규격 차이"}
      </Badge>
      {diff.matchedTokens.map((t) => (
        <span key={`m-${t}`} className='inline-flex items-center gap-0.5'>
          <Check className='size-3' />
          {t}
        </span>
      ))}
      {diff.missingTokens.map((t) => (
        <span
          key={`x-${t}`}
          className='inline-flex items-center gap-0.5 text-destructive'
          title='입력 규격에만 있음'
        >
          <Plus className='size-3' />
          {t}
        </span>
      ))}
      {diff.extraTokens.map((t) => (
        <span
          key={`e-${t}`}
          className='inline-flex items-center gap-0.5 text-muted-foreground'
          title='품목 규격에만 있음'
        >
          <Minus className='size-3' />
          {t}
        </span>
      ))}
    </div>
  );
};

/** 이미지 비교 검색 추천 리스트. 기존 품목으로 입고하거나 새 품목으로 등록할 수 있다. */
export const ItemRecommendations = ({
  data,
  onSelectItem,
  onCreateNew,
}: ItemRecommendationsProps) => (
  <div className='flex flex-col gap-3'>
    <div className='flex flex-wrap items-center justify-between gap-2'>
      <p className='text-sm text-muted-foreground'>
        후보 {data.candidateItemCount}개 품목 중 유사한 {data.results.length}개
      </p>
      <Button type='button' variant='outline' onClick={onCreateNew}>
        새 품목 등록
      </Button>
    </div>

    {data.results.length === 0 && (
      <p className='rounded-lg border p-4 text-sm text-muted-foreground'>
        비슷한 품목이 없습니다. 새 품목으로 등록하세요.
      </p>
    )}

    {data.results.map((r) => (
      <Card key={r.itemId} size='sm'>
        <CardContent className='flex flex-col gap-3 sm:flex-row'>
          <div className='flex gap-2'>
            <ZoomableImage
              path={r.representativeImage.path}
              alt={`${r.name} 대표 사진`}
              className='size-24'
            />
            <div className='flex flex-wrap content-start gap-1'>
              {r.additionalImages.map((img, i) => (
                <ZoomableImage
                  key={img.id}
                  path={img.path}
                  alt={`${r.name} 추가 사진 ${i + 1}`}
                  className='size-11'
                />
              ))}
            </div>
          </div>

          <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='font-semibold'>{r.name}</span>
              <span className='text-xs text-muted-foreground'>{r.code}</span>
              <ItemCategoryBadge category={r.category} />
              <Badge variant='outline'>유사도 {Math.round(r.score * 100)}%</Badge>
            </div>
            <p className='text-sm'>규격: {r.spec ?? "-"}</p>
            <p className='text-sm text-muted-foreground'>
              보유 {Number(r.onHandQuantity)} {r.baseUnit} · 가용{" "}
              {Number(r.availableQuantity)} {r.baseUnit}
            </p>
            <SpecDiff diff={r.specDiff} />
          </div>

          <div className='flex items-start'>
            <Button type='button' onClick={() => onSelectItem(r)}>
              이 품목으로 입고
            </Button>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);
